if (!customElements.get("notify-me")) {
  const PENDING_KEY = "one8:notify-me:pending";
  const STATUS_KEY = "one8:notify-me:status";
  const SUBSCRIBED_KEY = "one8:notify-me:subscribed";

  const PENDING_TTL_MS = 15 * 60 * 1000;
  const STATUS_TTL_MS = 5 * 60 * 1000;
  const REQUEST_TIMEOUT_MS = 10_000;

  const CUSTOMER_ID_KEYS = ["kp_customer_id", "kpCustomerId", "kp-customer-id"];
  const CUSTOMER_ID_RE = /^\d{5,20}$/;

  function readStore(store, key) {
    try {
      return window[store].getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      window[store].setItem(key, value);
    } catch (error) {
    }
  }

  function removeStore(store, key) {
    try {
      window[store].removeItem(key);
    } catch (error) {
    }
  }

  function readJson(store, key) {
    const raw = readStore(store, key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      removeStore(store, key);
      return null;
    }
  }

  function decodeEntities(text) {
    if (typeof text !== "string" || !text.includes("&")) return text;
    try {
      const doc = new DOMParser().parseFromString(text, "text/html");
      return doc.documentElement.textContent || text;
    } catch (error) {
      return text;
    }
  }

  function readKwikpassCustomerId() {
    for (const store of ["localStorage", "sessionStorage"]) {
      for (const key of CUSTOMER_ID_KEYS) {
        const raw = readStore(store, key);
        if (!raw) continue;

        const id = String(raw).replace(/^"|"$/g, "").trim().split("/").pop();
        if (CUSTOMER_ID_RE.test(id)) return id;
      }
    }

    // Native Shopify session fallback — Kwikpass only operates in India, so
    // international shoppers log in through the hosted Shopify login and
    // their customer id comes from Shopify's page context.
    const sessionCid = String((window.__st && window.__st.cid) || "");
    if (CUSTOMER_ID_RE.test(sessionCid)) return sessionCid;

    return "";
  }

  function isIndiaVisitor() {
    // Primary signal: the active Shopify Market country for this visitor,
    // decided server-side by Shopify's geo routing.
    const marketCountry = String(
      (window.Shopify && window.Shopify.country) || "",
    ).toUpperCase();
    if (marketCountry) return marketCountry === "IN";

    // Fallback when the Shopify global is unavailable: device timezone.
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (/kolkata|calcutta/i.test(tz)) return true;
    } catch (error) {
    }
    return new Date().getTimezoneOffset() === -330;
  }

  function redirectToShopifyLogin() {
    const returnTo = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    const locale = encodeURIComponent(
      (window.Shopify && window.Shopify.locale) || "en",
    );
    window.location = `/customer_authentication/login?return_to=${returnTo}&locale=${locale}`;
  }

  function waitForCustomerId(timeoutMs = 5000, intervalMs = 250) {
    return new Promise((resolve) => {
      const started = Date.now();

      const tick = () => {
        const id = readKwikpassCustomerId();
        if (id) return resolve(id);
        if (Date.now() - started >= timeoutMs) return resolve("");
        setTimeout(tick, intervalMs);
      };
      tick();
    });
  }

  function readPendingRecord() {
    const pending = readJson("localStorage", PENDING_KEY);
    if (!pending || !pending.url) return null;
    if (!(Date.now() - Number(pending.at) < PENDING_TTL_MS)) {
      removeStore("localStorage", PENDING_KEY);
      return null;
    }
    return pending;
  }

  function buttonLabel(button) {
    if (!button) return "";
    return String(button.innerText || button.textContent || "")
      .replace(/\s+/g, " ")
      .trim();
  }

  function announceSubscribed(button, label, productHandle, customerId) {
    document.dispatchEvent(
      new CustomEvent("notify-me:subscribed", {
        detail: { button, label, productHandle, customerId },
      }),
    );
  }

  function writeStatusCache(customerId, handle) {
    const cache = readJson("sessionStorage", STATUS_KEY) || {};
    cache[`${customerId}|${handle}`] = { subscribed: true, at: Date.now() };
    writeStore("sessionStorage", STATUS_KEY, JSON.stringify(cache));
  }

  // Completes a pending subscribe right where the shopper is (e.g. the PLP
  // they logged in from) and flips that product's cards to the subscribed
  // state, so no redirect to the product page is needed.
  async function completePendingInPlace(pending, customerId, endpoint) {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerId,
          productHandle: pending.handle,
        }),
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Request failed");
      }

      const cardButton = document.querySelector(
        `product-card[data-product-handle="${CSS.escape(pending.handle)}"] .card-product__button--notify`,
      );
      const label = buttonLabel(cardButton);

      rememberSubscribed(customerId, pending.handle);
      writeStatusCache(customerId, pending.handle);
      syncWaitlistCards();
      announceSubscribed(cardButton, label, pending.handle, customerId);
    } catch (error) {
      console.warn("[notify-me] pending subscribe failed", error);
    }
  }

  // If the shopper logged in on a different page than the product they wanted
  // to be notified about, complete the subscribe in place when possible; the
  // redirect to the saved product URL remains only as a fallback.
  function returnToPendingProduct() {
    const pending = readPendingRecord();
    if (!pending) return;
    const customerId = readKwikpassCustomerId();
    if (!customerId) return; // not logged in yet — nothing to do

    // A notify-me element for this product is already on the page (PDP, or an
    // open quick-add modal) — it resumes the pending subscribe itself.
    if (
      pending.handle &&
      document.querySelector(
        `notify-me[data-product-handle="${CSS.escape(pending.handle)}"]`,
      )
    ) {
      return;
    }

    const endpoint = window.variantStrings?.notifyEndpoint;
    if (endpoint && pending.handle) {
      removeStore("localStorage", PENDING_KEY);
      completePendingInPlace(pending, customerId, endpoint);
      return;
    }

    let targetPath;
    try {
      targetPath = new URL(pending.url, window.location.origin).pathname;
    } catch (error) {
      removeStore("localStorage", PENDING_KEY);
      return;
    }

    // Already on the target page but no notify-me element rendered for it —
    // the record can never resolve, so drop it instead of leaving a redirect
    // trap behind.
    if (window.location.pathname === targetPath) {
      removeStore("localStorage", PENDING_KEY);
      return;
    }

    // Redirect at most once per record. If we already sent the shopper to the
    // product page and the record still exists, something there failed to
    // resolve it — drop it rather than trapping them on every navigation.
    if (pending.redirected) {
      removeStore("localStorage", PENDING_KEY);
      return;
    }

    pending.redirected = true;
    writeStore("localStorage", PENDING_KEY, JSON.stringify(pending));

    window.location.href = pending.url;
  }

  function subscribedLabel() {
    return decodeEntities(
      window.variantStrings?.notifySubscribed || "You'll be notified",
    );
  }

  function rememberSubscribed(customerId, handle) {
    if (!customerId || !handle) return;
    const map = readJson("localStorage", SUBSCRIBED_KEY) || {};
    map[`${customerId}|${handle}`] = true;
    writeStore("localStorage", SUBSCRIBED_KEY, JSON.stringify(map));
  }

  function isRememberedSubscribed(customerId, handle) {
    if (!customerId || !handle) return false;
    const map = readJson("localStorage", SUBSCRIBED_KEY) || {};
    return map[`${customerId}|${handle}`] === true;
  }

  function subscribedLabelShort() {
    return decodeEntities(
      window.variantStrings?.notifySubscribedShort || "You're in",
    );
  }

  function flipCardButton(button) {
    const label = subscribedLabel();
    button.classList.add("disabled");
    button.setAttribute("aria-label", label);

    // The subscribed state renders without the bell, so drop it here too —
    // otherwise a card flipped in place keeps an icon the reloaded page won't have.
    button.querySelectorAll("img").forEach((icon) => icon.remove());

    const desktopText = button.querySelector(
      ".card-product__button-label--desktop",
    );
    const mobileText = button.querySelector(
      ".card-product__button-label--mobile",
    );

    if (desktopText || mobileText) {
      if (desktopText) desktopText.textContent = label;
      if (mobileText) mobileText.textContent = subscribedLabelShort();
      return;
    }

    const text = button.querySelector("span");
    if (text) text.textContent = label;
  }

  // Flips waitlist CTAs on product cards to the subscribed state for products
  // this customer has already joined, without needing the quick-add popup.
  function syncWaitlistCards() {
    const customerId = readKwikpassCustomerId();
    if (!customerId) return;

    document
      .querySelectorAll("product-card .card-product__button--notify")
      .forEach((button) => {
        const handle = button.closest("product-card")?.dataset.productHandle;
        if (!isRememberedSubscribed(customerId, handle)) return;
        flipCardButton(button);
      });
  }

  function onCardsReady() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", syncWaitlistCards);
    } else {
      syncWaitlistCards();
    }
    document.addEventListener("kp-data-sent", syncWaitlistCards);
    document.addEventListener("product-card:replaced", syncWaitlistCards);
  }

  onCardsReady();

  function onLoginReturn() {
    // Run after Kwikpass fires its login event, and again on page load in case
    // login triggered a full reload/redirect elsewhere on the site.
    document.addEventListener("kp-data-sent", returnToPendingProduct);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", returnToPendingProduct);
    } else {
      returnToPendingProduct();
    }
  }

  onLoginReturn();

  customElements.define(
    "notify-me",
    class NotifyMe extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector("[data-notify-button]");
        this.text = this.querySelector("[data-notify-text]");
        this.message = this.querySelector("[data-notify-message]");
        this.addToCartButton = this.buyButtons?.querySelector(
          "[data-add-to-cart], [data-add-to-wishlist]",
        );

        if (!this.button || !this.addToCartButton) return;

        this.onClick = this.onClick.bind(this);
        this.sync = this.sync.bind(this);
        this.onLoginSuccess = this.onLoginSuccess.bind(this);
        this.onSiblingSubscribed = this.onSiblingSubscribed.bind(this);

        this.button.addEventListener("click", this.onClick);
        document.addEventListener("kp-data-sent", this.onLoginSuccess);
        document.addEventListener(
          "notify-me:subscribed",
          this.onSiblingSubscribed,
        );

        this.observer = new MutationObserver(this.sync);
        this.observer.observe(this.addToCartButton, {
          attributes: true,
          attributeFilter: ["disabled"],
        });

        if (this.resumePending()) this.statusChecked = true;
        this.sync();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.button?.removeEventListener("click", this.onClick);
        document.removeEventListener("kp-data-sent", this.onLoginSuccess);
        document.removeEventListener(
          "notify-me:subscribed",
          this.onSiblingSubscribed,
        );
      }

      get buyButtons() {
        return this.closest(".buy-buttons, [data-variant-buy-buttons]");
      }

      get isSoldOut() {
        return this.addToCartButton.hasAttribute("disabled");
      }

      get productHandle() {
        return this.dataset.productHandle || "";
      }

      get isSubscribed() {
        return this.dataset.subscribed === "true";
      }

      get customerId() {
        return this.dataset.customerId || readKwikpassCustomerId();
      }

      sync() {
        const soldOut = this.isSoldOut;
        this.classList.toggle("notify-me--hidden", !soldOut);
        this.addToCartButton.classList.toggle(
          "buy-buttons__atc--hidden",
          soldOut,
        );

        if (soldOut && !this.statusChecked) {
          this.statusChecked = true;
          this.checkStatus();
        }
      }

      onClick() {
        if (this.isSubscribed || this.isBusy) return;

        if (this.customerId) {
          this.subscribe();
          return;
        }

        this.rememberPending();
        this.openLogin();
      }

      openLogin() {
        // Kwikpass only works for shoppers in India — everyone else goes to
        // the hosted Shopify login and returns with a Shopify session.
        if (!isIndiaVisitor()) {
          redirectToShopifyLogin();
          return;
        }

        if (typeof window.handleKpAndShopifyLogin === "function") {
          window.handleKpAndShopifyLogin();
          return;
        }

        setTimeout(() => {
          if (typeof window.handleKpAndShopifyLogin === "function") {
            window.handleKpAndShopifyLogin();
          } else {
            redirectToShopifyLogin();
          }
        }, 500);
      }

      async onLoginSuccess() {
        if (!this.readPending() || this.isSubscribed || this.isBusy) return;

        const customerId = await waitForCustomerId();
        if (!customerId || !this.isConnected) return;
        if (!this.readPending() || this.isSubscribed || this.isBusy) return;

        this.clearPending();
        this.subscribe();
      }

      onSiblingSubscribed(event) {
        const detail = event.detail || {};
        if (detail.button === this.button) return;
        if (detail.productHandle !== this.productHandle) return;
        if (this.isSubscribed || this.isBusy) return;

        this.markSubscribed();
      }

      rememberPending() {
        writeStore(
          "localStorage",
          PENDING_KEY,
          JSON.stringify({
            handle: this.productHandle,
            url: this.productUrl,
            at: Date.now(),
          }),
        );
      }

      get productUrl() {
        return (
          this.dataset.productUrl || `/products/${this.productHandle}`
        );
      }

      readPending() {
        const pending = readJson("localStorage", PENDING_KEY);
        if (!pending) return false;
        if (pending.handle !== this.productHandle) return false;

        if (!(Date.now() - Number(pending.at) < PENDING_TTL_MS)) {
          this.clearPending();
          return false;
        }
        return true;
      }

      clearPending() {
        removeStore("localStorage", PENDING_KEY);
      }

      resumePending() {
        if (!this.readPending()) return false;
        if (this.isSubscribed || !this.customerId) return false;

        this.clearPending();
        this.subscribe();
        return true;
      }

      statusCacheKey(customerId) {
        return `${customerId}|${this.productHandle}`;
      }

      readCachedStatus(customerId) {
        const cache = readJson("sessionStorage", STATUS_KEY);
        const entry = cache?.[this.statusCacheKey(customerId)];
        if (!entry) return null;
        if (!(Date.now() - Number(entry.at) < STATUS_TTL_MS)) return null;
        return entry.subscribed === true;
      }

      writeCachedStatus(customerId, subscribed) {
        const cache = readJson("sessionStorage", STATUS_KEY) || {};
        cache[this.statusCacheKey(customerId)] = { subscribed, at: Date.now() };
        writeStore("sessionStorage", STATUS_KEY, JSON.stringify(cache));
      }

      async checkStatus() {
        const endpoint = this.dataset.endpoint;
        if (!endpoint || this.isSubscribed || this.isBusy || !this.isSoldOut) {
          return;
        }

        const customerId = this.customerId;
        if (!customerId) return;

        const cached = this.readCachedStatus(customerId);
        if (cached !== null) {
          if (cached) this.markSubscribed();
          return;
        }

        const params = new URLSearchParams({
          productHandle: this.productHandle,
          customerId,
        });

        try {
          const response = await fetch(`${endpoint}?${params}`, {
            method: "GET",
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok || !result.ok) return;

          this.writeCachedStatus(customerId, result.subscribed === true);
          if (result.subscribed === true) this.markSubscribed();
        } catch (error) {
          console.warn("[notify-me] status check failed", error);
        }
      }

      async subscribe() {
        const endpoint = this.dataset.endpoint;
        const customerId = this.customerId;
        if (!endpoint || !customerId) return;

        this.setBusy(true);
        this.showMessage("", false);

        try {
          const response = await fetch(endpoint, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customerId,
              productHandle: this.productHandle,
            }),
            signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
          });

          const result = await response.json().catch(() => ({}));

          if (!response.ok || !result.ok) {
            throw new Error(result.error || "Request failed");
          }

          const label = buttonLabel(this.button);
          this.writeCachedStatus(customerId, true);
          this.markSubscribed();
          announceSubscribed(this.button, label, this.productHandle, customerId);
        } catch (error) {
          console.error("[notify-me]", error);
          this.showMessage(this.strings.notifyError, true);
        } finally {
          // Clear the pending record whether the attempt succeeded or failed —
          // a surviving record turns into a site-wide redirect trap. On
          // failure the shopper just clicks the button again.
          this.clearPending();
          this.setBusy(false);
        }
      }

      markSubscribed() {
        this.dataset.subscribed = "true";
        this.button.setAttribute("disabled", "disabled");
        if (this.text) this.text.textContent = this.strings.notifySubscribed;

        rememberSubscribed(this.customerId, this.productHandle);
        this.updateCardButtons();
      }

      // Flips any product-card waitlist CTAs for this product that are already
      // rendered on the page (PLP grid, carousels) to the subscribed state
      updateCardButtons() {
        const handle = this.productHandle;
        if (!handle) return;

        document
          .querySelectorAll(
            `product-card[data-product-handle="${CSS.escape(handle)}"] .card-product__button--notify`,
          )
          .forEach((button) => flipCardButton(button));
      }

      setBusy(busy) {
        this.isBusy = busy;
        if (busy) {
          this.button.setAttribute("aria-busy", "true");
        } else {
          this.button.removeAttribute("aria-busy");
        }
      }

      showMessage(text, isError) {
        if (!this.message) return;
        this.message.classList.toggle(
          "notify-me__message--error",
          Boolean(text) && isError,
        );
        this.message.textContent = text || "";
      }

      get strings() {
        const merged = {
          notifyMe: "Notify Me",
          notifySubscribed: "You'll be notified",
          notifySuccess: "Thank you! We'll email you as soon as it's back in stock.",
          notifyAlready: "Thank you! You're already on the list for this product.",
          notifyError: "Something went wrong. Please try again.",
          ...(window.variantStrings || {}),
        };

        for (const key of Object.keys(merged)) {
          merged[key] = decodeEntities(merged[key]);
        }
        return merged;
      }
    },
  );
}